// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import type { ProposedAppointment } from '@polarissleep/scheduling-next';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import { getIntakeMedplumClient } from '../lib/bot-client';
import { botIdentifier } from '../lib/bot-identifier';

const MAX_INTAKE_SLOT_ATTEMPTS = 2;
const INTAKE_SLOT_RETRY_DELAY_MS = 250;

interface UseIntakeSlotsOptions {
  uniqueId: string | undefined;
  fromDate: string | undefined;
  toDate: string | undefined;
}

interface UseIntakeSlotsResult {
  appointments: IntakeAppointmentCandidate[];
  appointmentType: AppointmentTypeDefinition | undefined;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

export interface IntakeAppointmentCandidate {
  appointment: ProposedAppointment;
  key: string;
  providerId: string;
  providerName: string;
  providerTimezone: string;
  serviceId: string;
  serviceName: string;
  serviceCode: string;
}

/**
 * Pre-auth equivalent of `useAvailableSlots`. Fetches intake-appointment slots through the
 * `find-intake-slots` bot using the public intake Medplum client (the patient is not yet
 * logged in; we authenticate them by their onboarding `uniqueId`).
 *
 * Fetches only when all parameters are provided.
 */
export function useIntakeSlots({ uniqueId, fromDate, toDate }: UseIntakeSlotsOptions): UseIntakeSlotsResult {
  const [appointments, setAppointments] = useState<IntakeAppointmentCandidate[]>([]);
  const [appointmentType, setAppointmentType] = useState<AppointmentTypeDefinition | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchSlots = useCallback(async () => {
    if (!uniqueId || !fromDate || !toDate) {
      setAppointments([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let result:
        | {
            appointments: IntakeAppointmentCandidate[];
            appointmentType: AppointmentTypeDefinition;
          }
        | undefined;
      let lastError: unknown;
      for (let attempt = 0; attempt < MAX_INTAKE_SLOT_ATTEMPTS; attempt += 1) {
        try {
          const client = await getIntakeMedplumClient();
          result = (await client.executeBot(
            botIdentifier('find-intake-slots'),
            { uniqueId, fromDate, toDate },
            'application/json'
          )) as {
            appointments: IntakeAppointmentCandidate[];
            appointmentType: AppointmentTypeDefinition;
          };
          break;
        } catch (err: unknown) {
          lastError = err;
          if (attempt + 1 < MAX_INTAKE_SLOT_ATTEMPTS) {
            await new Promise<void>((resolve) => window.setTimeout(resolve, INTAKE_SLOT_RETRY_DELAY_MS));
          }
        }
      }
      if (!result) {
        throw lastError instanceof Error ? lastError : new Error('Failed to load available slots');
      }
      setAppointments(result.appointments);
      setAppointmentType(result.appointmentType);
    } catch (err) {
      Sentry.captureException(err);
      setError(err instanceof Error ? err : new Error('Failed to load available slots'));
    } finally {
      setLoading(false);
    }
  }, [uniqueId, fromDate, toDate]);

  useEffect(() => {
    fetchSlots().catch(console.error);
  }, [fetchSlots]);

  return { appointments, appointmentType, loading, error, refresh: fetchSlots };
}
